/**
 * Pink — Insights › Tiempos de cocina. Del par V131 order_items.fired_at→ready_at:
 * por platillo (promedio + MEDIANA + conteo + p90 + más lento), por hora del día
 * (hora LOCAL de México, UTC-6 fijo en el server) y las órdenes más lentas.
 * Reusa DatePeriodFilter como el resto de Insights; sucursal = la activa de la sesión.
 * Responsive desde el inicio (modelo QuoteLinesEditor): tablas en overflow-x-auto,
 * grids con breakpoint, sin tabla→tarjetas.
 */
import { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Clock, Timer, ChefHat, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { DatePeriodFilter, periodToQuery, periodLabel, defaultPeriod, type PeriodValue } from '../components/DatePeriodFilter';

interface DishRow { name: string; kind: string; count: number; avgSec: number; medianSec: number; p90Sec: number; maxSec: number; }
interface HourRow { hour: number; count: number; avgSec: number; }
interface SlowOrder { id: string; closedAt: string; tableName: string | null; totalSec: number; itemCount: number; items: string; }
interface KitchenTimes {
  from: string; to: string;
  quality: { firedItems: number; firedNoReady: number; pctNoReady: number };
  byDish: DishRow[]; byHour: HourRow[]; slowest: SlowOrder[];
}

/** Segundos → "1m 23s" / "45s". Los tiempos de cocina son de minutos, no de dinero. */
const fmtDur = (sec: any) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  const m = Math.floor(s / 60);
  return m ? `${m}m ${s % 60}s` : `${s}s`;
};
const int = (v: any) => Number(v ?? 0).toLocaleString('es-MX');

/** Mensaje único de estado vacío — el vacío es INSTRUCCIÓN, no una pantalla rota. */
function EmptyKitchen({ compact = false }: { compact?: boolean }) {
  return (
    <div className={`flex flex-col items-center gap-1.5 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 text-center text-slate-500 ${compact ? 'px-4 py-6' : 'px-6 py-10'}`}>
      <ChefHat className="h-6 w-6 text-slate-300" />
      <p className="text-sm font-medium text-slate-600">Aún no hay tiempos registrados</p>
      <p className="max-w-xs text-xs text-slate-400">Se generan cuando la cocina marca los platillos como <strong>listos</strong> (botón “Listo” en la vista de Cocina).</p>
    </div>
  );
}

function Card({ title, icon, children, action }: { title: string; icon?: any; children: any; action?: any }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-serif text-lg text-slate-900">{icon}{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

type SortKey = 'name' | 'count' | 'avgSec' | 'medianSec' | 'p90Sec' | 'maxSec';

export function KitchenTimesView({ sourceModule = 'gastro' }: { sourceModule?: 'gastro' | 'boutique' | 'gym' }) {
  const { user } = useAuth();
  // V169 (🅓 por estación) — con dos estaciones, pestañas Cocina/Barra; con una línea,
  // sin pestañas y sin filtro → la vista es EXACTAMENTE la de hoy (sin columna vacía).
  const twoStations = !!user?.tenant?.kitchenTwoStations;
  const [station, setStation] = useState<'cocina' | 'barra'>('cocina');
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod());
  const [data, setData] = useState<KitchenTimes | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('avgSec');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const qs = new URLSearchParams(periodToQuery(period));
    qs.set('sourceModule', sourceModule);
    if (twoStations) qs.set('station', station); // sin dos estaciones: no se filtra (todo)
    api(`/pos/insights/kitchen-times?${qs.toString()}`)
      .then((r) => { if (!cancelled) setData(r as KitchenTimes); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, sourceModule, twoStations, station]);

  const sortedDish = useMemo(() => {
    const rows = data?.byDish ? [...data.byDish] : [];
    rows.sort((a, b) => {
      const av = a[sortKey]; const bv = b[sortKey];
      const cmp = typeof av === 'string' ? String(av).localeCompare(String(bv)) : Number(av) - Number(bv);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir]);

  const hoursWithData = useMemo(() => (data?.byHour ?? []).filter((h) => h.count > 0), [data]);
  const busyHour = useMemo(() => hoursWithData.slice().sort((a, b) => b.avgSec - a.avgSec)[0], [hoursWithData]);
  const chartHours = useMemo(() => (data?.byHour ?? []).filter((h) => h.hour >= 6 && h.hour <= 23), [data]);
  const hasAny = (data?.byDish?.length ?? 0) > 0;

  const th = (key: SortKey, label: string, align = 'right') => (
    <th className={`px-3 py-2 ${align === 'right' ? 'text-right' : 'text-left'} cursor-pointer select-none whitespace-nowrap hover:text-slate-700`}
        onClick={() => { if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); } }}>
      {label}{sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl tracking-tight text-slate-900">Tiempos de cocina</h2>
          <p className="text-sm text-slate-500">Cuánto tarda tu cocina: por platillo, por hora y qué órdenes se salieron de lo normal — {periodLabel(period)}.</p>
        </div>
        <DatePeriodFilter value={period} onChange={setPeriod} />
      </div>

      {/* V169 (🅓 por estación) — pestañas Cocina/Barra solo con dos estaciones. */}
      {twoStations && (
        <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
          {(['cocina', 'barra'] as const).map((s) => (
            <button key={s} onClick={() => setStation(s)}
              className={`rounded-lg px-4 py-1.5 text-sm font-medium capitalize transition ${station === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Calidad del dato — ítems mandados a cocina y nunca marcados listos (excluidos). */}
      {data && data.quality.firedNoReady > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-2.5 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            <strong>{int(data.quality.firedNoReady)}</strong> platillo(s) mandados a cocina y nunca marcados como listos
            ({data.quality.pctNoReady}% de {int(data.quality.firedItems)}). <span className="text-amber-700">Excluidos de los promedios.</span>
          </span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : (
        <>
          {/* 1 · Por platillo */}
          <Card title="Por platillo" icon={<Timer className="h-4 w-4 text-slate-500" />}
            action={<span className="text-[11px] text-slate-400">Promedio y mediana: si difieren mucho, “tardó una vez” vs “siempre tarda”.</span>}>
            {sortedDish.length === 0 ? <EmptyKitchen /> : (
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                    <tr>
                      {th('name', 'Platillo', 'left')}
                      {th('count', 'Veces')}
                      {th('avgSec', 'Promedio')}
                      {th('medianSec', 'Mediana')}
                      {th('p90Sec', 'p90')}
                      {th('maxSec', 'Más lento')}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sortedDish.map((r) => (
                      <tr key={`${r.kind}-${r.name}`}>
                        <td className="px-3 py-2 font-medium text-slate-800">{r.name}
                          <span className={`ml-2 rounded px-1 py-0.5 text-[9px] uppercase ${r.kind === 'service' ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-700'}`}>{r.kind === 'service' ? 'serv' : 'prod'}</span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{int(r.count)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{fmtDur(r.avgSec)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{fmtDur(r.medianSec)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtDur(r.p90Sec)}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtDur(r.maxSec)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* 2 · Por hora del día */}
            <Card title="Por hora del día" icon={<Clock className="h-4 w-4 text-slate-500" />}
              action={busyHour ? <span className="text-[11px] text-slate-400">Más lenta: {busyHour.hour}:00 ({fmtDur(busyHour.avgSec)})</span> : undefined}>
              {hoursWithData.length === 0 ? <EmptyKitchen compact /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartHours} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} interval={1} tickFormatter={(h: number) => `${h}`} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={44} tickFormatter={(v: number) => `${Math.round(v / 60)}m`} />
                    <Tooltip formatter={(v: any) => fmtDur(v)} labelFormatter={(h) => `${h}:00 h (local)`} />
                    <Bar dataKey="avgSec" radius={[3, 3, 0, 0]}>
                      {chartHours.map((h, i) => <Cell key={i} fill={busyHour && h.hour === busyHour.hour ? '#b45309' : '#cbd5e1'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              <p className="mt-1 text-[11px] text-slate-400">Hora local de México. Promedio de preparación por hora en que se mandó a cocina.</p>
            </Card>

            {/* 3 · Órdenes más lentas */}
            <Card title="Órdenes más lentas" icon={<Timer className="h-4 w-4 text-rose-500" />}>
              {(data?.slowest?.length ?? 0) === 0 ? <EmptyKitchen compact /> : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Fecha y hora</th>
                        <th className="px-3 py-2">Mesa</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2">Llevaba</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(data?.slowest ?? []).map((o) => (
                        <tr key={o.id}>
                          <td className="px-3 py-2 whitespace-nowrap text-slate-600">{new Date(o.closedAt).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</td>
                          <td className="px-3 py-2 text-slate-600">{o.tableName ?? <span className="text-slate-400">Para llevar</span>}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{fmtDur(o.totalSec)}</td>
                          <td className="px-3 py-2 text-slate-500"><span className="line-clamp-2">{o.items || `${o.itemCount} ítem(s)`}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>

          {!hasAny && data && (
            <p className="text-center text-[11px] text-slate-400">
              El flujo de cocina (mandar a cocina → marcar listo) es de V131; los tiempos empiezan a aparecer aquí conforme la cocina lo use.
            </p>
          )}
        </>
      )}
    </div>
  );
}
